import { ADMIN_VIEW_PREFIX, RoleLink, type RolePath } from "@/components/client-role";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

/** Strip the admin-view prefix so the trail can be written once against the viewer paths. */
function toViewerPath(pathname: string): string {
  if (pathname === ADMIN_VIEW_PREFIX) return "/";
  if (pathname.startsWith(`${ADMIN_VIEW_PREFIX}/`)) return pathname.slice(ADMIN_VIEW_PREFIX.length);
  return pathname;
}

// Home is the root of every trail. Its label is the short "Overview" (matching the nav tab), not the page's
// longer H1 ("Recruitment overview") — so the crumb never just echoes the heading below it.
const HOME: { label: string; to: RolePath } = { label: "Overview", to: "/" };

// A role is reachable from several origins; the middle crumb reflects where you actually came from (`?from`).
const TIER_CRUMB: Record<string, { label: string; to: RolePath }> = {
  csuite: { label: "C-Suite", to: "/csuite" },
  "vp-director": { label: "VP / Director", to: "/vp-director" },
  general: { label: "General", to: "/general" },
};

// Current-page label for the non-role pages (the role page passes its title in via `currentLabel`).
const PAGE_LABEL: Record<string, string> = {
  "/": "Overview",
  "/csuite": "C-Suite",
  "/vp-director": "VP / Director",
  "/general": "General",
  "/activity": "Activity",
  "/attention": "Needs attention",
  "/team": "Team",
  "/roles/new": "New role",
};

type Crumb = { key: string; label: string; roleTo?: RolePath; to?: string };

const LINK_CLS =
  "rounded text-muted-foreground/80 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";

/** The path from home to here, rendered directly above the page H1 — the primary wayfinding affordance for a
 *  drilled-in page (top-left, where the eye lands). It sits inside {@link PageHeader}, so it's present on
 *  EVERY page at a fixed height: the H1 never shifts vertically between pages (the standing constant-header
 *  rule) and there's no empty spacer anywhere. The first crumb is always home; a role's middle crumb reflects
 *  the `?from` origin (Needs attention / a tier), so "back" returns you to where you actually came from rather
 *  than always to the overview. */
export function Breadcrumb({ currentLabel }: { currentLabel?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const from = useRouterState({ select: (s) => (s.location.search as { from?: string }).from });
  const path = toViewerPath(pathname);
  const isRole = path.startsWith("/search/");

  const crumbs: Crumb[] = [];
  if (path === "/") {
    // At home — a single, non-link crumb keeps the line present (constant height) without echoing the H1.
    crumbs.push({ key: "home", label: HOME.label });
  } else {
    crumbs.push({ key: "home", label: HOME.label, roleTo: HOME.to });
    if (isRole) {
      if (from === "attention") {
        crumbs.push({ key: "origin", label: "Needs attention", to: `${ADMIN_VIEW_PREFIX}/attention` });
      } else if (from && TIER_CRUMB[from]) {
        const t = TIER_CRUMB[from];
        crumbs.push({ key: "origin", label: t.label, roleTo: t.to });
      }
      crumbs.push({ key: "current", label: currentLabel ?? "Role" });
    } else {
      crumbs.push({ key: "current", label: PAGE_LABEL[path] ?? currentLabel ?? "" });
    }
  }

  return (
    <nav aria-label="Breadcrumb" className="mb-2 flex items-center gap-1 text-xs leading-none">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={c.key} className="flex min-w-0 items-center gap-1">
            {i > 0 ? <ChevronRight className="size-3 shrink-0 text-muted-foreground/40" aria-hidden /> : null}
            {last ? (
              <span className="truncate font-medium text-muted-foreground" aria-current="page">
                {c.label}
              </span>
            ) : c.roleTo ? (
              <RoleLink to={c.roleTo} activeOptions={{ exact: true }} className={LINK_CLS}>
                {c.label}
              </RoleLink>
            ) : (
              <Link to={c.to as never} className={LINK_CLS}>
                {c.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
